import React, { useCallback } from "react"
import { useTranslation } from "react-i18next"
import Stack from "@mui/material/Stack"
import { CREATE_ITEM, OWNER } from "constants/index"
import { useAppSelector } from "appRedux/hooks"
import useWishItems from "hooks/useWishItems"
import Banner from "components/atoms/Banner"
import WishListView from "components/templates/WishListView"

type WishItemsListProps = {
  showAddNewItemButton?: boolean;
  totalCount?: number;
}

export default function WishItemsList(props: WishItemsListProps) {
  const { showAddNewItemButton, totalCount = 0 } = props

  const items = useAppSelector((state) => state.profileTabs.items)
  const role = useAppSelector((state) => state.global.role)
  const isPremium = useAppSelector((state) => state.user.profile?.isPremium)
  const premiumItemCountLimit = useAppSelector((state) => state.initSlice.premiumLimit?.itemCount)
  const activePremiumFeatures = useAppSelector((state) => state.premium.activePremiumFeatures)

  const { isFetching } = useWishItems()

  const { t } = useTranslation()

  const shouldRenderMaxVisibleItemsBanner = useCallback(() => {
    if (role === OWNER) return false
    return !isFetching && totalCount - items.length > 0
  }, [items.length, totalCount, isFetching])

  const shouldRenderProItemCountLimitBanner = useCallback(() => {
    if (role !== OWNER) return false
    if (!activePremiumFeatures.hasOwnProperty(CREATE_ITEM)) return false
    return !isPremium && !isFetching && premiumItemCountLimit && items.length >= premiumItemCountLimit
  }, [isPremium, items.length, isFetching, premiumItemCountLimit])

  return (
    <Stack flexGrow={1}>
      {shouldRenderMaxVisibleItemsBanner() && <Banner
          severity="info"
          message={t("items.visibleItemsCount", { count: items.length })}
          style={{ marginBottom: "16px" }}
      />}
      {shouldRenderProItemCountLimitBanner() && <Banner
          severity="warning"
          message={t("premium.upgradeToProForMoreItemCreation")}
          isProLimit={true}
          feature={CREATE_ITEM}
          style={{ marginBottom: "16px" }}
      />}
      <WishListView
        list={items}
        isLoading={isFetching}
        showAddNewItemButton={showAddNewItemButton}
      />
    </Stack>
  )
}
